// Typed client for the Artisan API. Types come from src/schema.ts, which is generated from
// the API's OpenAPI document (`pnpm api:generate`); the drift check in scripts/test.sh fails
// when they no longer match the API code.
import createClient, { type Client, type ClientOptions } from "openapi-fetch";

import type { components, paths } from "./schema";

export type { components, paths };
export type Schemas = components["schemas"];
export type ApiClient = Client<paths>;

/** The platform's one error body: {"error": {"code", "message", "request_id", "details"?}}. */
export type ErrorBody = Schemas["ErrorBody"];

export class ApiError extends Error {
  readonly status: number;
  /** Stable machine-readable code; switch on this, never on the message. */
  readonly code: string;
  readonly requestId: string | null;
  readonly details: ErrorBody["details"];

  constructor(status: number, body: Partial<ErrorBody> | undefined) {
    super(body?.message ?? `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.code = body?.code ?? (status >= 500 ? "server_error" : "http_error");
    this.requestId = body?.request_id ?? null;
    this.details = body?.details ?? null;
  }
}

/** Reads the platform error body from anything an openapi-fetch call returned as `error`. */
export function toApiError(status: number, error: unknown): ApiError {
  const body =
    error && typeof error === "object" && "error" in error
      ? ((error as { error: Partial<ErrorBody> }).error ?? undefined)
      : undefined;
  return new ApiError(status, body);
}

interface FetchResult {
  data?: unknown;
  error?: unknown;
  response: Response;
}

/**
 * Returns the data of a successful call or throws ApiError.
 *
 *   const me = await unwrap(api.GET("/v1/me"));
 */
export async function unwrap<R extends FetchResult>(call: Promise<R>): Promise<NonNullable<R["data"]>> {
  const result = await call;
  if (result.error !== undefined || !result.response.ok) {
    throw toApiError(result.response.status, result.error);
  }
  return result.data as NonNullable<R["data"]>;
}

export function createApiClient(options: ClientOptions & { baseUrl: string }): ApiClient {
  return createClient<paths>(options);
}
